import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { fmtDate, fmtRelative } from "../lib/format";
import {
  PageHeader, Panel, Chip, DsButton, Drawer, DataTable,
} from "../ds";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const FILTERS = [
  { key: "active",          label: "Active",          q: { status: "new,reviewed" } },
  { key: "product_interest",label: "Product interest", q: { classification: "product_interest" } },
  { key: "custom_build",    label: "Custom build",    q: { classification: "custom_build" } },
  { key: "support",         label: "Support",         q: { classification: "support" } },
  { key: "spam",            label: "Spam",            q: { classification: "spam" } },
  { key: "all",             label: "All",             q: {} },
];

function dubai2line(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const date = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dubai", day: "numeric", month: "short",
  }).format(d);
  const time = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dubai", hour: "2-digit", minute: "2-digit", hour12: false,
  }).format(d);
  return `${date}, ${time}`;
}

export default function Inbound() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [filterKey, setFilterKey] = useState("active");
  const [active, setActive] = useState(null);
  const [notes, setNotes] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pickingProject, setPickingProject] = useState(false);
  const [projectId, setProjectId] = useState("");

  const { data: projects } = useQuery({
    queryKey: ["projects-mini"],
    queryFn: async () => (await api.get("/projects")).data,
  });

  const filterQ = FILTERS.find((f) => f.key === filterKey)?.q || {};
  const { data } = useQuery({
    queryKey: ["inbound", filterKey],
    queryFn: async () => {
      const { data } = await api.get("/inbound", { params: {} });
      return data;
    },
    refetchInterval: 20000,
  });

  const allRows = data?.inbound || [];
  const rows = allRows.filter((r) => {
    if (filterQ.status) {
      const allowed = filterQ.status.split(",");
      if (!allowed.includes(r.status)) return false;
    }
    if (filterQ.classification && r.classification !== filterQ.classification) return false;
    return true;
  });

  const openRow = async (r) => {
    const full = (await api.get(`/inbound/${r.id}`)).data.inbound;
    setActive(full);
    setNotes(full.notes || "");
  };
  const refresh = () => qc.invalidateQueries({ queryKey: ["inbound"] });
  const patch = async (patchBody) => {
    try {
      await api.patch(`/inbound/${active.id}`, patchBody);
      refresh();
      const full = (await api.get(`/inbound/${active.id}`)).data.inbound;
      setActive(full);
      toast.success("Saved.");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const privacyDelete = async () => {
    try {
      await api.delete(`/inbound/${active.id}`);
      toast.success("Deleted permanently.");
      setConfirmDelete(false);
      setActive(null);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const createTask = async () => {
    if (!projectId) return toast.error("Pick a project first.");
    try {
      const r = await api.post(`/inbound/${active.id}/create-task`, { project_id: projectId });
      toast.success("Task created.");
      setPickingProject(false);
      setProjectId("");
      navigate(`/tasks/${r.data.task_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-10 max-w-[1600px] mx-auto space-y-6">
      <PageHeader title="Inbound" subtitle="Contact-form requests, classified by Sales Lead" />

      <div className="flex gap-2 flex-wrap" data-testid="inbound-filters">
        {FILTERS.map((f) => (
          <DsButton
            key={f.key}
            variant={filterKey === f.key ? "primary" : "default"}
            onClick={() => setFilterKey(f.key)}
            data-testid={`inbound-filter-${f.key}`}
          >
            {f.label}
          </DsButton>
        ))}
      </div>

      <Panel testId="inbound-panel">
        <DataTable
          testId="inbound-table"
          columns={[
            { key: "received_at", label: "Received", mono: true, width: 150,
              render: (r) => dubai2line(r.received_at) },
            { key: "from", label: "From", width: 220,
              render: (r) => (
                <div>
                  <div className="font-semibold text-[color:var(--fg)]">{r.name || "—"}</div>
                  <div className="text-[color:var(--muted)] text-xs">{r.company || "—"}</div>
                </div>
              ) },
            { key: "sector", label: "Sector",
              render: (r) => r.sector || "—" },
            { key: "country", label: "Country", width: 110,
              render: (r) => r.country || "—" },
            { key: "classification", label: "Type", width: 160,
              render: (r) => <Chip value={r.classification} /> },
            { key: "summary", label: "Summary",
              render: (r) => <span className="text-[color:var(--muted)]">{r.summary || "—"}</span> },
            { key: "status", label: "Status", width: 110,
              render: (r) => <Chip value={r.status} /> },
          ]}
          rows={rows}
          onRowClick={openRow}
          empty="No requests in this view."
        />
      </Panel>

      <Drawer open={!!active} onClose={() => setActive(null)} testId="inbound-drawer">
        {active && (
          <>
            <div className="font-mono text-xs text-[color:var(--muted)] mb-1">
              {dubai2line(active.received_at)} · ref {String(active.id).slice(-8)}
            </div>
            <h2 className="font-ui text-xl font-bold text-[color:var(--fg)]">
              {(active.name || "—")} · {(active.company || "—")}
            </h2>
            <div className="mt-2 flex gap-2 flex-wrap">
              <Chip value={active.classification} />
              <Chip value={active.status} />
              {active.urgency && (
                <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-[color:var(--muted)] px-[6px] py-[1px] border border-[color:var(--line-2)] rounded-[4px]">
                  urgency: {active.urgency}
                </span>
              )}
            </div>

            <dl className="mt-6 grid grid-cols-[90px_1fr] gap-y-2 text-sm">
              <dt className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em]">SECTOR</dt>
              <dd className="text-[color:var(--fg)]">{active.sector || "—"}</dd>
              <dt className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em]">COUNTRY</dt>
              <dd className="text-[color:var(--fg)]">{active.country || "—"}</dd>
              <dt className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em]">PRODUCT</dt>
              <dd className="text-[color:var(--fg)]">{active.product || "—"}</dd>
              <dt className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em]">SUMMARY</dt>
              <dd className="text-[color:var(--fg)]">{active.summary || "—"}</dd>
            </dl>

            <div className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em] mt-6">
              MESSAGE (PLAIN TEXT)
            </div>
            <pre
              className="mt-2 font-ui whitespace-pre-wrap break-words text-sm text-[color:var(--fg)] bg-[color:var(--panel-2)] border border-[color:var(--line)] rounded-[7px] p-3 select-text"
              data-testid="inbound-message"
            >{active.message || "(no message)"}</pre>
            <p className="font-mono text-[10px] text-[color:var(--muted)] mt-1">
              Rendered as plain text. Links are not clickable.
            </p>

            {active.need && (
              <>
                <div className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em] mt-5">NEED</div>
                <p className="text-sm text-[color:var(--fg)] mt-1">{active.need}</p>
              </>
            )}

            <div className="font-mono text-[10.5px] uppercase text-[color:var(--muted)] tracking-[0.1em] mt-5">NOTES (CEO)</div>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="inbound-notes"
              className="mt-2 bg-[color:var(--panel-2)] border-[color:var(--line-2)]"
            />
            <DsButton
              className="mt-2"
              onClick={() => patch({ notes })}
              data-testid="inbound-save-notes"
            >
              Save notes
            </DsButton>

            <div className="mt-6 flex flex-wrap gap-2">
              <DsButton onClick={() => patch({ status: "reviewed" })} data-testid="inbound-mark-reviewed">
                Mark reviewed
              </DsButton>
              <DsButton variant="primary" onClick={() => setPickingProject(true)} data-testid="inbound-create-task">
                Create task
              </DsButton>
              <DsButton onClick={() => patch({ status: "archived" })} data-testid="inbound-archive">
                Archive
              </DsButton>
              <DsButton variant="no" onClick={() => setConfirmDelete(true)} data-testid="inbound-delete">
                Delete permanently
              </DsButton>
            </div>
          </>
        )}
      </Drawer>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent data-testid="inbound-delete-confirm" className="bg-[color:var(--panel)] border-[color:var(--line)]">
          <DialogHeader>
            <DialogTitle style={{ color: "var(--bad)" }}>Delete permanently?</DialogTitle>
            <DialogDescription>
              This fulfils a data-deletion request. The entry is removed immediately. An audit entry is written WITHOUT any personal fields.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DsButton variant="ghost" onClick={() => setConfirmDelete(false)}>Cancel</DsButton>
            <DsButton variant="no" onClick={privacyDelete} data-testid="inbound-delete-confirm-btn">
              Yes, delete
            </DsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pickingProject} onOpenChange={setPickingProject}>
        <DialogContent data-testid="inbound-pick-project" className="bg-[color:var(--panel)] border-[color:var(--line)]">
          <DialogHeader>
            <DialogTitle>Create task — pick a project</DialogTitle>
          </DialogHeader>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger data-testid="inbound-project-select"><SelectValue placeholder="Project" /></SelectTrigger>
            <SelectContent>
              {(projects?.projects || []).map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <DsButton variant="ghost" onClick={() => setPickingProject(false)}>Cancel</DsButton>
            <DsButton variant="primary" onClick={createTask} data-testid="inbound-create-task-confirm">
              Create
            </DsButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
